import {
	createToolEngineInterfaceToken,
	type BaseToolEngineInterface,
	type ToolEngineInterfaceToken,
} from "@shared/GenericTools/EngineInterfaces/ToolEngineInterfaceToken";
import type {
	FormModelInterface,
	ToolEngineDataValue,
	ToolEngineOptions,
	ToolEngineValidationResult,
	EnginePayloadData,
	ToolDefinitionSnapshotData,
	ToolEngineConfigData,
	ToolEngineGroupData,
	ToolEngineItemData,
	ToolGraphEdgeData,
	ToolGraphNodeData,
	ToolEngineStateData,
	ToolInstanceSnapshotData,
	ToolResultSnapshotData,
} from "@shared/GenericTools/EngineInterfaces/ToolEngineDataTypes";

export type ToolConfig = ToolEngineConfigData
export type EnginePayload = EnginePayloadData

export interface FormSchemaEngineInterface extends BaseToolEngineInterface {
	normalizeReporterAssessmentConfig(config: ToolConfig): ToolConfig
	getReporterAssessmentConfigErrors(config: ToolConfig): string[]
	addReporterAssessmentRole(config: ToolConfig, roleId: string, questionId: string): ToolConfig
	duplicateReporterAssessmentRole(config: ToolConfig, sourceRoleId: string, newRoleId: string, createId: (kind: string) => string): ToolConfig
	removeReporterAssessmentRole(config: ToolConfig, roleId: string): ToolConfig
	addReporterAssessmentDomain(config: ToolConfig, roleId: string, domainId: string): ToolConfig
	removeReporterAssessmentDomain(config: ToolConfig, roleId: string, domainId: string): ToolConfig
	addReporterAssessmentQuestion(config: ToolConfig, roleId: string, questionId: string): ToolConfig
	removeReporterAssessmentQuestion(config: ToolConfig, roleId: string, questionId: string): ToolConfig
	moveReporterAssessmentQuestion(config: ToolConfig, roleId: string, index: number, offset: number): ToolConfig
	addReporterAssessmentScaleOption(config: ToolConfig): ToolConfig
	removeReporterAssessmentScaleOption(config: ToolConfig, index: number): ToolConfig
	markPairedQuestionnaireAdapted(config: ToolConfig): ToolConfig
	addPairedQuestionnaireSection(config: ToolConfig): ToolConfig
	removePairedQuestionnaireSection(config: ToolConfig, sectionId: string): ToolConfig
	movePairedQuestionnaireSection(config: ToolConfig, index: number, offset: number): ToolConfig
	addPairedQuestionnaireQuestion(config: ToolConfig, sectionId: string): ToolConfig
	removePairedQuestionnaireQuestion(config: ToolConfig, sectionId: string, questionId: string): ToolConfig
	movePairedQuestionnaireQuestion(config: ToolConfig, sectionId: string, index: number, offset: number): ToolConfig
	movePairedQuestionnaireQuestionToSection(config: ToolConfig, sectionId: string, questionId: string, destinationId: string): ToolConfig
	addPairedQuestionnaireOption(config: ToolConfig): ToolConfig
	removePairedQuestionnaireOption(config: ToolConfig, optionId: string): ToolConfig
	movePairedQuestionnaireOption(config: ToolConfig, index: number, offset: number): ToolConfig
	setPairedQuestionnaireRatingMaximum(config: ToolConfig, maximum: number): ToolConfig
	setPairedQuestionnaireResponseKind(config: ToolConfig, kind: string): ToolConfig
	updateHandoutReady(config: ToolConfig, ready: boolean): ToolConfig
	editHandoutCollection(config: ToolConfig, kind: string, operation: string, index: number, sectionId: string): ToolConfig
	updateFormChoiceList(config: ToolConfig, listName: string, fieldId: string, operation: string, index?: number, value?: ToolConfig): ToolConfig
	addFormScriptOption(config: ToolConfig, option: ToolConfig): ToolConfig
	removeFormScriptOption(config: ToolConfig, optionId: string): ToolConfig
	moveFormScriptOption(config: ToolConfig, index: number, offset: number): ToolConfig
	addFormScriptMode(config: ToolConfig): ToolConfig
	removeFormScriptMode(config: ToolConfig, modeId: string): ToolConfig
	moveFormScriptMode(config: ToolConfig, index: number, offset: number): ToolConfig
	normalizePositionMapConfig(config: ToolConfig): ToolConfig
	getPositionMapSections(config: ToolConfig): ToolConfig[]
	normalizeConfig(config?: ToolConfig): ToolConfig
	validateConfig(config?: ToolConfig): ToolEngineValidationResult
	addFormSection(config: ToolConfig, section: ToolConfig): ToolConfig
	removeFormSection(config: ToolConfig, sectionId: string): ToolConfig
	addFormField(config: ToolConfig, field: ToolConfig): ToolConfig
	addFormRepeatRow(config: ToolConfig, groupId: string): ToolConfig
	removeFormField(config: ToolConfig, fieldId: string): ToolConfig
	moveFormConfigItem(config: ToolConfig, listName: "sections" | "fields" | "minimumGroups", index: number, offset: number): ToolConfig
	setFormRatingMaximum(config: ToolConfig, maximum: number, fieldIds?: string[]): ToolConfig
	getFormSections(config: ToolConfig): ToolConfig[]
	getStructuredFormView(config: ToolConfig, answers: ToolConfig): ToolConfig
	createFormItemFromToolConfig(config?: ToolConfig, options?: ToolEngineOptions): FormModelInterface
	createToolConfigFromFormItem(formItem?: FormModelInterface | null): ToolConfig
}

export interface FormResponseEngineInterface extends BaseToolEngineInterface {
	getReporterAssessmentView(config?: ToolConfig, payload?: EnginePayload, reporterId?: string): ToolConfig
	getReporterAssessmentResultView(config?: ToolConfig, result?: ToolConfig, priorResults?: ToolConfig[]): ToolConfig
	getFormResultView(config?: ToolConfig, result?: ToolConfig, priorResults?: ToolConfig[]): ToolConfig
	getPairedQuestionnaireReport(config: ToolConfig, reports: ToolConfig): ToolConfig
	getPairedQuestionnaireResultView(config: ToolConfig, result: ToolConfig, priorResults: ToolConfig[]): ToolConfig
	updatePairedQuestionnaireAnswer(payload: EnginePayload, config: ToolConfig, partnerId: string, questionId: string, optionId: string | null): EnginePayload
	updatePairedQuestionnaireName(payload: EnginePayload, config: ToolConfig, partnerId: string, name: string): EnginePayload
	getHandoutView(config: ToolConfig, payload: EnginePayload): ToolConfig
	updateFormDiscreteRating(payload: EnginePayload, config: ToolConfig, choiceId?: string | null, status?: string): EnginePayload
	getFormDiscreteRatingReview(result: ToolConfig, priorResults: ToolConfig[], fallbackConfig: ToolConfig): ToolConfig
	getAnchoredMeanReport(config: ToolConfig, answers: ToolConfig): ToolConfig
	getAnchoredMeanResultView(config: ToolConfig | undefined, result: ToolConfig, priorResults: ToolConfig[]): ToolConfig
	getFormScriptView(config: ToolConfig, payload: EnginePayload): ToolConfig
	updateFormScriptSelection(payload: EnginePayload, config: ToolConfig, optionId: string, selected: boolean): EnginePayload
	updateFormScriptReady(payload: EnginePayload, config: ToolConfig, isReady: boolean): EnginePayload
	getAnchoredFormRatingReview(result: ToolConfig, priorResults: ToolConfig[], fallbackConfig: ToolConfig): ToolConfig
	getPositionMapResultView(config: ToolConfig | undefined | null, result: ToolConfig, priorResults: ToolConfig[]): ToolConfig
	getReflectiveStatementResultView(config: ToolConfig | undefined | null, result: ToolConfig, priorResults: ToolConfig[]): ToolConfig
	formatFormResultDate(value: unknown): string
	getPositionMapCompletion(config: ToolConfig, answers: ToolConfig): ToolConfig
	getMostRecentPositionResult(results: ToolConfig[]): ToolConfig | null
	getFrequencyInventoryReport(inventory: ToolConfig, answers: ToolConfig): ToolConfig
	getSubscaleProfileResult(answers: ToolConfig, config: ToolConfig): ToolConfig
	getOrdinalProfileStatus(config: ToolConfig, answers: ToolConfig): ToolConfig
	getOrdinalProfileDetails(config: ToolConfig, answers: ToolConfig, includeEmpty?: boolean): ToolConfig[]
	getOrdinalProfileComparisons(config: ToolConfig, results: ToolConfig[]): ToolConfig[]
	getArchivedOrdinalProfileItems(payload: EnginePayload, config: ToolConfig): ToolConfig[]
	getFrequencyInventoryResultView(config: ToolConfig | undefined, result: ToolConfig, priorResults: ToolConfig[]): ToolConfig
	getSubscaleProfileResultView(config: ToolConfig | undefined, result: ToolConfig, priorResults: ToolConfig[]): ToolConfig
	createDefaultPayload(config?: ToolConfig): EnginePayload
	normalizePayload(payload?: EnginePayload, config?: ToolConfig): EnginePayload
	validateFormPayload(payload?: EnginePayload, config?: ToolConfig): ToolEngineValidationResult
	createSummaryInput(payload?: EnginePayload, config?: ToolConfig): EnginePayload
	createScoredSummaryInput(payload?: EnginePayload, config?: ToolConfig): EnginePayload
	createFormEnginePayload(enginePayload?: EnginePayload, formItem?: FormModelInterface | null): EnginePayload
	updateFormAnswer(payload: EnginePayload, config: ToolConfig, fieldId: string, answer: string | number | null): EnginePayload
}

export interface ItemLifecycleEngineInterface extends BaseToolEngineInterface {
	normalizeConfig(config?: ToolConfig, definition?: ToolDefinitionSnapshotData | null): ToolConfig
	createDefaultPayload(config?: ToolConfig, definition?: ToolDefinitionSnapshotData | null): EnginePayload
	normalizePayload(payload?: EnginePayload, config?: ToolConfig, definition?: ToolDefinitionSnapshotData | null): EnginePayload
	validateConfig(config?: ToolConfig, definition?: ToolDefinitionSnapshotData | null): ToolEngineValidationResult
	validateItemListPayload(payload?: EnginePayload, config?: ToolConfig, definition?: ToolDefinitionSnapshotData | null): ToolEngineValidationResult
	createSummaryInput(payload?: EnginePayload, config?: ToolConfig, definition?: ToolDefinitionSnapshotData | null): EnginePayload
}

export interface ItemCollectionEngineInterface extends ItemLifecycleEngineInterface {
	createItemListConfigFromToolConfig(config?: ToolConfig): ToolConfig
	createToolConfigFromItemListConfig(itemListConfig?: ToolConfig): ToolConfig
	createItemListEnginePayload(enginePayload?: EnginePayload, itemListConfig?: ToolConfig, itemState?: ToolConfig | null): EnginePayload
	createItemListGroup(group?: ToolConfig, index?: number): ToolEngineGroupData
	createItemListItem(item?: ToolConfig, index?: number): ToolEngineItemData
	ensureItemState(itemState?: ToolConfig, items?: ToolEngineItemData[]): ToolEngineStateData
	getItemListUnselectedItems(itemListConfig?: ToolConfig, itemState?: ToolConfig): ToolEngineItemData[]
	createItemListResultConfig(toolResult?: ToolResultSnapshotData | null, toolInstance?: ToolInstanceSnapshotData | null): ToolConfig
	createItemListResultState(toolResult?: ToolResultSnapshotData | null): ToolEngineStateData
}

export interface ItemGroupingEngineInterface extends BaseToolEngineInterface {
	updateItemListItemGroup(itemState?: ToolConfig, itemId?: string, groupId?: string): ToolConfig
	groupItemListItems(itemListConfig?: ToolConfig, itemState?: ToolConfig): ToolEngineGroupData[]
}

export interface ItemSelectionEngineInterface extends BaseToolEngineInterface {
	updateItemListItemSelection(itemState?: ToolConfig, itemId?: string, isSelected?: boolean): ToolConfig
	getItemListSelectedItems(itemListConfig?: ToolConfig, itemState?: ToolConfig): ToolEngineItemData[]
	getItemListUnselectedItems(itemListConfig?: ToolConfig, itemState?: ToolConfig): ToolEngineItemData[]
}

export interface ItemRankingEngineInterface extends BaseToolEngineInterface {
	createRankedItemState(itemState?: ToolConfig, orderedItemIds?: string[]): ToolConfig
	getItemListOrderedItems(itemListConfig?: ToolConfig, itemState?: ToolConfig): ToolEngineItemData[]
	getItemListUnrankedItems(itemListConfig?: ToolConfig, itemState?: ToolConfig): ToolEngineItemData[]
}

export interface ItemRatingEngineInterface extends ItemLifecycleEngineInterface {
	createItemRatingConfigFromToolConfig(config?: ToolConfig): ToolConfig
	createToolConfigFromItemRatingConfig(itemRatingConfig?: ToolConfig): ToolConfig
	createItemRatingEnginePayload(enginePayload?: EnginePayload, itemRatingConfig?: ToolConfig): EnginePayload
	createItemRatingWorkConfig(config?: ToolConfig, payload?: EnginePayload): ToolConfig
	compareItemRatingProfiles(current?: EnginePayload, previous?: EnginePayload): ToolConfig
	createItemRatingResultConfig(toolResult?: ToolResultSnapshotData | null, toolInstance?: ToolInstanceSnapshotData | null): ToolConfig
	createItemRatingItem(item?: ToolConfig, index?: number): ToolEngineItemData
	getAnsweredItemRatingItems(itemRatingConfig?: ToolConfig): ToolEngineItemData[]
	getItemRatingAverage(itemRatingConfig?: ToolConfig): number | null
	getItemRatingConfiguredSum(itemRatingConfig?: ToolConfig): number | null
	hasItemRating(item?: ToolConfig): boolean
	updateItemRating(itemRatingConfig?: ToolConfig, itemId?: string, rating?: ToolEngineDataValue): ToolConfig
	createItemRatingPreviewConfig(itemRatingConfig?: ToolConfig): ToolConfig
}

export interface ItemNotesEngineInterface extends BaseToolEngineInterface {
	getItemRatingNoteTitle(item?: ToolConfig, scaleMax?: number): string
	updateItemRatingItem(itemRatingConfig?: ToolConfig, itemId?: string, changes?: ToolConfig): ToolConfig
}

export interface ItemWeightingEngineInterface extends BaseToolEngineInterface {}
export interface ItemPositioningEngineInterface extends BaseToolEngineInterface {}
export interface ItemCriteriaEngineInterface extends BaseToolEngineInterface {}

export interface TrackerSchemaEngineInterface extends BaseToolEngineInterface {
	normalizeSudsConfig(config?: ToolConfig): ToolConfig
	normalizeMomentaryConfig(config?: ToolConfig): ToolConfig
	getMomentarySchedule(config?: ToolConfig): ToolConfig[]
	normalizeConfig(config?: ToolConfig): ToolConfig
	validateConfig(config?: ToolConfig): ToolEngineValidationResult
	normalizeEpisodeConfig(config?: ToolConfig): ToolConfig
	formatEpisodeDate(value: string | Date | null): string
	getTrackerTemplateSummaryInput(config?: ToolConfig): ToolConfig
	createTrackerConfigFromToolConfig(config?: ToolConfig): ToolConfig
	createToolConfigFromTrackerConfig(trackerConfig?: ToolConfig): ToolConfig
	createEntryFormItemFromTrackerConfig(trackerConfig?: ToolConfig, options?: ToolEngineOptions): FormModelInterface
	syncTrackerConfigFromEntryForm(trackerConfig?: ToolConfig, formItem?: FormModelInterface | null): ToolConfig
	createTrackerResultConfig(toolResult?: ToolResultSnapshotData | null, toolInstance?: ToolInstanceSnapshotData | null): ToolConfig
	createWeeklyDiaryConfigFromToolConfig(config?: ToolConfig): ToolConfig
	createToolConfigFromWeeklyDiaryConfig(weeklyDiaryConfig?: ToolConfig): ToolConfig
}

export interface TrackerEntryEngineInterface extends BaseToolEngineInterface {
	normalizeSudsPayload(payload?: EnginePayload, config?: ToolConfig): EnginePayload
	summarizeSudsSeries(payload?: EnginePayload, config?: ToolConfig): ToolConfig[]
	normalizeMomentaryPayload(payload?: EnginePayload, config?: ToolConfig): EnginePayload
	updateMomentaryStatus(payload?: EnginePayload, config?: ToolConfig, momentId?: string, status?: string, now?: number, optionId?: string): EnginePayload
	advanceMomentarySession(payload?: EnginePayload, config?: ToolConfig, now?: number): EnginePayload
	summarizeMomentarySession(payload?: EnginePayload, config?: ToolConfig): ToolConfig
	createDefaultPayload(config?: ToolConfig): EnginePayload
	normalizePayload(payload?: EnginePayload, config?: ToolConfig): EnginePayload
	validateTrackerPayload(payload?: EnginePayload, config?: ToolConfig, isSubmission?: boolean): ToolEngineValidationResult
	createSummaryInput(payload?: EnginePayload, config?: ToolConfig): EnginePayload
	createEpisodePayload(payload?: EnginePayload, config?: ToolConfig, entries?: ToolConfig[], options?: {includeEmpty?: boolean}): EnginePayload
	summarizeEpisodes(entries?: ToolConfig[], config?: ToolConfig): ToolConfig
	sortEpisodeEntries(entries: ToolConfig[], config: ToolConfig, sortBy?: string): ToolConfig[]
	groupEpisodeEntries(entries: ToolConfig[], groupings?: Record<string, string>): {label: string, entries: ToolConfig[]}[]
	validateEpisodeEntries(entries?: ToolConfig[], config?: ToolConfig): ToolEngineValidationResult
	createTrackerEnginePayload(enginePayload?: EnginePayload, trackerConfig?: ToolConfig, formItem?: FormModelInterface | null): EnginePayload
	getTrackerResultEntry(toolResult?: ToolResultSnapshotData | null): ToolConfig
	getPrimaryMeasureValue(entry?: ToolConfig, trackerConfig?: ToolConfig): ToolEngineDataValue
	createWeeklyDiaryEnginePayload(enginePayload?: EnginePayload, trackerConfig?: ToolConfig, entries?: ToolConfig[]): EnginePayload
	getWeeklyDiaryResultEntries(toolResult?: ToolResultSnapshotData | null): ToolConfig[]
	createWeeklyDiaryEntry(trackerConfig?: ToolConfig, entry?: ToolConfig, index?: number): ToolConfig
}

export interface TrackerSchedulingEngineInterface extends BaseToolEngineInterface {}
export interface TrackerQueueEngineInterface extends BaseToolEngineInterface {}

export interface StagedWorksheetSchemaEngineInterface extends BaseToolEngineInterface {
	createStagedWorksheetConfigFromToolConfig(config?: ToolConfig): ToolConfig
	createContextMapConfig(source?: ToolConfig): ToolConfig
	createToolConfigFromStagedWorksheetConfig(stagedWorksheetConfig?: ToolConfig): ToolConfig
	createStagedWorksheetResultConfig(toolResult?: ToolResultSnapshotData | null, toolInstance?: ToolInstanceSnapshotData | null): ToolConfig
}

export interface StagedWorksheetEntryEngineInterface extends BaseToolEngineInterface {
	getStagedWorksheetInventoryView(payload?: EnginePayload, config?: ToolConfig): ToolConfig
	getLinkedRequirementView(payload?: EnginePayload, config?: ToolConfig): ToolConfig
	getLinkedRequirementChanges(payload?: EnginePayload, previousPayload?: EnginePayload, config?: ToolConfig, previousConfig?: ToolConfig): ToolConfig
	getEarlierLinkedRequirementResult(current?: ToolConfig, candidates?: ToolConfig[]): ToolConfig | null
	updateLinkedRequirementWork(payload?: EnginePayload, config?: ToolConfig, changes?: ToolConfig): EnginePayload
	createStagedWorksheetEnginePayload(enginePayload?: EnginePayload, stagedWorksheetConfig?: ToolConfig): EnginePayload
	getContextMapView(payload?: EnginePayload, config?: ToolConfig): ToolConfig
	getContextMapRevisionHistory(current?: ToolConfig, priorResults?: ToolConfig[], config?: ToolConfig): ToolConfig[]
	updateContextMapPromptAnswer(payload?: EnginePayload, config?: ToolConfig, promptId?: string, answer?: string): EnginePayload
	updateContextMapLinkStatus(payload?: EnginePayload, config?: ToolConfig, itemId?: string, statusId?: string): EnginePayload
	addContextMapAlternative(payload?: EnginePayload, config?: ToolConfig): EnginePayload
	updateContextMapAlternative(payload?: EnginePayload, config?: ToolConfig, id?: string, text?: string): EnginePayload
	removeContextMapAlternative(payload?: EnginePayload, config?: ToolConfig, id?: string): EnginePayload
	moveContextMapAlternative(payload?: EnginePayload, config?: ToolConfig, id?: string, direction?: number): EnginePayload
	getOrderedStagedWorksheetEntries(enginePayload?: EnginePayload, stagedWorksheetConfig?: ToolConfig): ToolConfig[]
	addStagedWorksheetEntry(enginePayload?: EnginePayload, stagedWorksheetConfig?: ToolConfig, stageId?: string): EnginePayload
	updateStagedWorksheetEntry(enginePayload?: EnginePayload, stagedWorksheetConfig?: ToolConfig, itemId?: string, changes?: ToolConfig): EnginePayload
	removeStagedWorksheetEntry(enginePayload?: EnginePayload, stagedWorksheetConfig?: ToolConfig, itemId?: string): EnginePayload
	moveStagedWorksheetEntry(enginePayload?: EnginePayload, stagedWorksheetConfig?: ToolConfig, itemId?: string, direction?: number): EnginePayload
	updateStagedWorksheetContext(enginePayload?: EnginePayload, stagedWorksheetConfig?: ToolConfig, changes?: ToolConfig): EnginePayload
	selectStagedWorksheetOption(enginePayload?: EnginePayload, stagedWorksheetConfig?: ToolConfig, stageId?: string, optionId?: string, isSelected?: boolean): EnginePayload
	hasStagedWorksheetAnchor(enginePayload?: EnginePayload, stagedWorksheetConfig?: ToolConfig): boolean
	createStagedWorksheetCategoryProfile(enginePayload?: EnginePayload, stagedWorksheetConfig?: ToolConfig): ToolConfig
	createStagedWorksheetStage(stage?: ToolConfig, index?: number): ToolConfig
	createStagedWorksheetField(field?: ToolConfig, index?: number): ToolConfig
	createStagedWorksheetItem(item?: ToolConfig, index?: number): ToolConfig
	moveStagedWorksheetItem(stage?: ToolConfig, fromIndex?: number, toIndex?: number): ToolConfig
	updateStagedWorksheetItem(stage?: ToolConfig, itemId?: string, changes?: ToolConfig): ToolConfig
	getStagedWorksheetNextItem(stage?: ToolConfig): ToolConfig | null
	getStagedWorksheetReadiness(enginePayload?: EnginePayload, config?: ToolConfig): string
	createStagedWorksheetAlternative(alternative?: ToolConfig, index?: number): ToolConfig
	createStagedWorksheetScoringSummary(enginePayload?: EnginePayload, stagedWorksheetConfig?: ToolConfig): ToolConfig
}

export interface DocumentStructureEngineInterface extends BaseToolEngineInterface {}
export interface DocumentActionEngineInterface extends BaseToolEngineInterface {}
export interface DocumentScheduleEngineInterface extends BaseToolEngineInterface {}
export interface DocumentDecisionEngineInterface extends BaseToolEngineInterface {}

export interface GraphSchemaEngineInterface extends BaseToolEngineInterface {
	createGraphConfigFromToolConfig(config?: ToolConfig): ToolConfig
	createToolConfigFromGraphConfig(graphConfig?: ToolConfig): ToolConfig
	createGraphEnginePayload(enginePayload?: EnginePayload, graphConfig?: ToolConfig): EnginePayload
	normalizeGraphPayload(enginePayload?: EnginePayload, graphConfig?: ToolConfig): EnginePayload
	validateGraphPayload(enginePayload?: EnginePayload, graphConfig?: ToolConfig): ToolEngineValidationResult
	createGraphResultConfig(toolResult?: ToolResultSnapshotData | null, toolInstance?: ToolInstanceSnapshotData | null): ToolConfig
}

export interface GraphNodeEngineInterface extends BaseToolEngineInterface {
	createGraphNode(node?: ToolConfig, index?: number): ToolGraphNodeData
	addGraphNode(enginePayload?: EnginePayload, node?: ToolConfig): EnginePayload
	updateGraphNode(enginePayload?: EnginePayload, nodeId?: string, changes?: ToolConfig): EnginePayload
	deleteGraphNode(enginePayload?: EnginePayload, nodeId?: string): EnginePayload
	getGraphNodes(enginePayload?: EnginePayload): ToolGraphNodeData[]
	findGraphNodeById(enginePayload?: EnginePayload, nodeId?: string): ToolGraphNodeData | null
}

export interface GraphEdgeEngineInterface extends BaseToolEngineInterface {
	createGraphEdge(edge?: ToolConfig, index?: number): ToolGraphEdgeData
	addGraphEdge(enginePayload?: EnginePayload, edge?: ToolConfig): EnginePayload
	updateGraphEdge(enginePayload?: EnginePayload, edgeId?: string, changes?: ToolConfig): EnginePayload
	deleteGraphEdge(enginePayload?: EnginePayload, edgeId?: string): EnginePayload
	getGraphEdges(enginePayload?: EnginePayload): ToolGraphEdgeData[]
	findGraphEdgeById(enginePayload?: EnginePayload, edgeId?: string): ToolGraphEdgeData | null
}

export interface GraphPlacementEngineInterface extends BaseToolEngineInterface {
	updateGraphNodePosition(enginePayload?: EnginePayload, nodeId?: string, position?: ToolConfig): EnginePayload
	updateGraphViewport(enginePayload?: EnginePayload, viewport?: ToolConfig): EnginePayload
	getGraphViewport(enginePayload?: EnginePayload): ToolConfig
}

export interface TimelineSchemaEngineInterface extends BaseToolEngineInterface {
	getTimelineResultConfig(payload?: EnginePayload, config?: ToolConfig): ToolConfig
	normalizeConfig(config?: ToolConfig): ToolConfig
	validateConfig(config?: ToolConfig): ToolEngineValidationResult
}
export interface TimelineEventEngineInterface extends BaseToolEngineInterface {
	createDefaultPayload(config?: ToolConfig): EnginePayload
	normalizePayload(payload?: EnginePayload, config?: ToolConfig): EnginePayload
	validateTimelinePayload(payload?: EnginePayload, config?: ToolConfig): ToolEngineValidationResult
	createSummaryInput(payload?: EnginePayload, config?: ToolConfig): EnginePayload
	createEvent(event?: ToolConfig): ToolConfig
	updateEvent(payload?: EnginePayload, event?: ToolConfig, config?: ToolConfig): EnginePayload
	removeEvent(payload?: EnginePayload, eventId?: string, config?: ToolConfig): EnginePayload
}
export interface TimelineOrderingEngineInterface extends BaseToolEngineInterface {
	getOrderedEvents(payload?: EnginePayload): ToolConfig[]
	moveEvent(payload?: EnginePayload, eventId?: string, direction?: number, config?: ToolConfig): EnginePayload
	getTemporalRelations(payload?: EnginePayload): ToolConfig[]
}

export interface CanvasTimelineEngineInterface extends BaseToolEngineInterface {
	normalizeConfig(config?: ToolConfig): ToolConfig
	normalizePayload(payload?: EnginePayload, config?: ToolConfig): EnginePayload
	addEvent(payload?: EnginePayload, event?: ToolConfig, config?: ToolConfig): EnginePayload
	updateEvent(payload?: EnginePayload, eventId?: string, changes?: ToolConfig, config?: ToolConfig): EnginePayload
	deleteEvent(payload?: EnginePayload, eventId?: string, config?: ToolConfig): EnginePayload
	moveEvent(payload?: EnginePayload, eventId?: string, target?: ToolConfig, config?: ToolConfig): EnginePayload
	assignStyleClass(payload?: EnginePayload, eventId?: string, classId?: string, config?: ToolConfig): EnginePayload
	setGroups(payload?: EnginePayload, groups?: ToolConfig[], config?: ToolConfig): EnginePayload
}

export interface CompositionObjectEngineInterface extends BaseToolEngineInterface {}
export interface CompositionTransformEngineInterface extends BaseToolEngineInterface {}
export interface CompositionLayerEngineInterface extends BaseToolEngineInterface {}
export interface CompositionStrokeEngineInterface extends BaseToolEngineInterface {}
export interface CompositionAnnotationEngineInterface extends BaseToolEngineInterface {}

export interface ExperimentPlanEngineInterface extends BaseToolEngineInterface {}
export interface ExperimentOutcomeEngineInterface extends BaseToolEngineInterface {}
export interface ExperimentLearningEngineInterface extends BaseToolEngineInterface {}

export interface HierarchyStructureEngineInterface extends BaseToolEngineInterface {}
export interface HierarchyPracticeEngineInterface extends BaseToolEngineInterface {}
export interface HierarchyProgressEngineInterface extends BaseToolEngineInterface {}

export interface GameRulesEngineInterface extends BaseToolEngineInterface {}
export interface GameSessionEngineInterface extends BaseToolEngineInterface {}
export interface GameTurnEngineInterface extends BaseToolEngineInterface {}
export interface GameActionEngineInterface extends BaseToolEngineInterface {}
export interface GameScoreEngineInterface extends BaseToolEngineInterface {}
export interface GameDeckEngineInterface extends BaseToolEngineInterface {}
export interface GameBoardEngineInterface extends BaseToolEngineInterface {}

export interface DataViewSourceEngineInterface extends BaseToolEngineInterface {}
export interface DataViewTransformEngineInterface extends BaseToolEngineInterface {}
export interface DataViewDerivedEngineInterface extends BaseToolEngineInterface {}

export const FormSchemaEngineInterfaceToken = createToolEngineInterfaceToken<FormSchemaEngineInterface>("formSchemaInterface")
export const FormResponseEngineInterfaceToken = createToolEngineInterfaceToken<FormResponseEngineInterface>("formResponseInterface")
export const ItemCollectionEngineInterfaceToken = createToolEngineInterfaceToken<ItemCollectionEngineInterface>("itemCollectionInterface")
export const ItemGroupingEngineInterfaceToken = createToolEngineInterfaceToken<ItemGroupingEngineInterface>("itemGroupingInterface")
export const ItemSelectionEngineInterfaceToken = createToolEngineInterfaceToken<ItemSelectionEngineInterface>("itemSelectionInterface")
export const ItemRankingEngineInterfaceToken = createToolEngineInterfaceToken<ItemRankingEngineInterface>("itemRankingInterface")
export const ItemRatingEngineInterfaceToken = createToolEngineInterfaceToken<ItemRatingEngineInterface>("itemRatingInterface")
export const ItemNotesEngineInterfaceToken = createToolEngineInterfaceToken<ItemNotesEngineInterface>("itemNotesInterface")
export const ItemWeightingEngineInterfaceToken = createToolEngineInterfaceToken<ItemWeightingEngineInterface>("itemWeightingInterface")
export const ItemPositioningEngineInterfaceToken = createToolEngineInterfaceToken<ItemPositioningEngineInterface>("itemPositioningInterface")
export const ItemCriteriaEngineInterfaceToken = createToolEngineInterfaceToken<ItemCriteriaEngineInterface>("itemCriteriaInterface")
export const TrackerSchemaEngineInterfaceToken = createToolEngineInterfaceToken<TrackerSchemaEngineInterface>("trackerSchemaInterface")
export const TrackerEntryEngineInterfaceToken = createToolEngineInterfaceToken<TrackerEntryEngineInterface>("trackerEntryInterface")
export const TrackerSchedulingEngineInterfaceToken = createToolEngineInterfaceToken<TrackerSchedulingEngineInterface>("trackerSchedulingInterface")
export const TrackerQueueEngineInterfaceToken = createToolEngineInterfaceToken<TrackerQueueEngineInterface>("trackerQueueInterface")
export const StagedWorksheetSchemaEngineInterfaceToken = createToolEngineInterfaceToken<StagedWorksheetSchemaEngineInterface>("stagedWorksheetSchemaInterface")
export const StagedWorksheetEntryEngineInterfaceToken = createToolEngineInterfaceToken<StagedWorksheetEntryEngineInterface>("stagedWorksheetEntryInterface")
export const DocumentStructureEngineInterfaceToken = createToolEngineInterfaceToken<DocumentStructureEngineInterface>("documentStructureInterface")
export const DocumentActionEngineInterfaceToken = createToolEngineInterfaceToken<DocumentActionEngineInterface>("documentActionInterface")
export const DocumentScheduleEngineInterfaceToken = createToolEngineInterfaceToken<DocumentScheduleEngineInterface>("documentScheduleInterface")
export const DocumentDecisionEngineInterfaceToken = createToolEngineInterfaceToken<DocumentDecisionEngineInterface>("documentDecisionInterface")
export const GraphSchemaEngineInterfaceToken = createToolEngineInterfaceToken<GraphSchemaEngineInterface>("graphSchemaInterface")
export const GraphNodeEngineInterfaceToken = createToolEngineInterfaceToken<GraphNodeEngineInterface>("graphNodeInterface")
export const GraphEdgeEngineInterfaceToken = createToolEngineInterfaceToken<GraphEdgeEngineInterface>("graphEdgeInterface")
export const GraphPlacementEngineInterfaceToken = createToolEngineInterfaceToken<GraphPlacementEngineInterface>("graphPlacementInterface")
export const TimelineSchemaEngineInterfaceToken = createToolEngineInterfaceToken<TimelineSchemaEngineInterface>("timelineSchemaInterface")
export const TimelineEventEngineInterfaceToken = createToolEngineInterfaceToken<TimelineEventEngineInterface>("timelineEventInterface")
export const TimelineOrderingEngineInterfaceToken = createToolEngineInterfaceToken<TimelineOrderingEngineInterface>("timelineOrderingInterface")
export const CanvasTimelineEngineInterfaceToken = createToolEngineInterfaceToken<CanvasTimelineEngineInterface>("canvasTimelineInterface")
export const CompositionObjectEngineInterfaceToken = createToolEngineInterfaceToken<CompositionObjectEngineInterface>("compositionObjectInterface")
export const CompositionTransformEngineInterfaceToken = createToolEngineInterfaceToken<CompositionTransformEngineInterface>("compositionTransformInterface")
export const CompositionLayerEngineInterfaceToken = createToolEngineInterfaceToken<CompositionLayerEngineInterface>("compositionLayerInterface")
export const CompositionStrokeEngineInterfaceToken = createToolEngineInterfaceToken<CompositionStrokeEngineInterface>("compositionStrokeInterface")
export const CompositionAnnotationEngineInterfaceToken = createToolEngineInterfaceToken<CompositionAnnotationEngineInterface>("compositionAnnotationInterface")
export const ExperimentPlanEngineInterfaceToken = createToolEngineInterfaceToken<ExperimentPlanEngineInterface>("experimentPlanInterface")
export const ExperimentOutcomeEngineInterfaceToken = createToolEngineInterfaceToken<ExperimentOutcomeEngineInterface>("experimentOutcomeInterface")
export const ExperimentLearningEngineInterfaceToken = createToolEngineInterfaceToken<ExperimentLearningEngineInterface>("experimentLearningInterface")
export const HierarchyStructureEngineInterfaceToken = createToolEngineInterfaceToken<HierarchyStructureEngineInterface>("hierarchyStructureInterface")
export const HierarchyPracticeEngineInterfaceToken = createToolEngineInterfaceToken<HierarchyPracticeEngineInterface>("hierarchyPracticeInterface")
export const HierarchyProgressEngineInterfaceToken = createToolEngineInterfaceToken<HierarchyProgressEngineInterface>("hierarchyProgressInterface")
export const GameRulesEngineInterfaceToken = createToolEngineInterfaceToken<GameRulesEngineInterface>("gameRulesInterface")
export const GameSessionEngineInterfaceToken = createToolEngineInterfaceToken<GameSessionEngineInterface>("gameSessionInterface")
export const GameTurnEngineInterfaceToken = createToolEngineInterfaceToken<GameTurnEngineInterface>("gameTurnInterface")
export const GameActionEngineInterfaceToken = createToolEngineInterfaceToken<GameActionEngineInterface>("gameActionInterface")
export const GameScoreEngineInterfaceToken = createToolEngineInterfaceToken<GameScoreEngineInterface>("gameScoreInterface")
export const GameDeckEngineInterfaceToken = createToolEngineInterfaceToken<GameDeckEngineInterface>("gameDeckInterface")
export const GameBoardEngineInterfaceToken = createToolEngineInterfaceToken<GameBoardEngineInterface>("gameBoardInterface")
export const DataViewSourceEngineInterfaceToken = createToolEngineInterfaceToken<DataViewSourceEngineInterface>("dataViewSourceInterface")
export const DataViewTransformEngineInterfaceToken = createToolEngineInterfaceToken<DataViewTransformEngineInterface>("dataViewTransformInterface")
export const DataViewDerivedEngineInterfaceToken = createToolEngineInterfaceToken<DataViewDerivedEngineInterface>("dataViewDerivedInterface")

export const ToolEngineInterfaceRegistry = Object.freeze({
	formSchemaInterface: FormSchemaEngineInterfaceToken,
	formResponseInterface: FormResponseEngineInterfaceToken,
	itemCollectionInterface: ItemCollectionEngineInterfaceToken,
	itemGroupingInterface: ItemGroupingEngineInterfaceToken,
	itemSelectionInterface: ItemSelectionEngineInterfaceToken,
	itemRankingInterface: ItemRankingEngineInterfaceToken,
	itemRatingInterface: ItemRatingEngineInterfaceToken,
	itemNotesInterface: ItemNotesEngineInterfaceToken,
	itemWeightingInterface: ItemWeightingEngineInterfaceToken,
	itemPositioningInterface: ItemPositioningEngineInterfaceToken,
	itemCriteriaInterface: ItemCriteriaEngineInterfaceToken,
	trackerSchemaInterface: TrackerSchemaEngineInterfaceToken,
	trackerEntryInterface: TrackerEntryEngineInterfaceToken,
	trackerSchedulingInterface: TrackerSchedulingEngineInterfaceToken,
	trackerQueueInterface: TrackerQueueEngineInterfaceToken,
	stagedWorksheetSchemaInterface: StagedWorksheetSchemaEngineInterfaceToken,
	stagedWorksheetEntryInterface: StagedWorksheetEntryEngineInterfaceToken,
	documentStructureInterface: DocumentStructureEngineInterfaceToken,
	documentActionInterface: DocumentActionEngineInterfaceToken,
	documentScheduleInterface: DocumentScheduleEngineInterfaceToken,
	documentDecisionInterface: DocumentDecisionEngineInterfaceToken,
	graphSchemaInterface: GraphSchemaEngineInterfaceToken,
	graphNodeInterface: GraphNodeEngineInterfaceToken,
	graphEdgeInterface: GraphEdgeEngineInterfaceToken,
	graphPlacementInterface: GraphPlacementEngineInterfaceToken,
	timelineSchemaInterface: TimelineSchemaEngineInterfaceToken,
	timelineEventInterface: TimelineEventEngineInterfaceToken,
	timelineOrderingInterface: TimelineOrderingEngineInterfaceToken,
	canvasTimelineInterface: CanvasTimelineEngineInterfaceToken,
	compositionObjectInterface: CompositionObjectEngineInterfaceToken,
	compositionTransformInterface: CompositionTransformEngineInterfaceToken,
	compositionLayerInterface: CompositionLayerEngineInterfaceToken,
	compositionStrokeInterface: CompositionStrokeEngineInterfaceToken,
	compositionAnnotationInterface: CompositionAnnotationEngineInterfaceToken,
	experimentPlanInterface: ExperimentPlanEngineInterfaceToken,
	experimentOutcomeInterface: ExperimentOutcomeEngineInterfaceToken,
	experimentLearningInterface: ExperimentLearningEngineInterfaceToken,
	hierarchyStructureInterface: HierarchyStructureEngineInterfaceToken,
	hierarchyPracticeInterface: HierarchyPracticeEngineInterfaceToken,
	hierarchyProgressInterface: HierarchyProgressEngineInterfaceToken,
	gameRulesInterface: GameRulesEngineInterfaceToken,
	gameSessionInterface: GameSessionEngineInterfaceToken,
	gameTurnInterface: GameTurnEngineInterfaceToken,
	gameActionInterface: GameActionEngineInterfaceToken,
	gameScoreInterface: GameScoreEngineInterfaceToken,
	gameDeckInterface: GameDeckEngineInterfaceToken,
	gameBoardInterface: GameBoardEngineInterfaceToken,
	dataViewSourceInterface: DataViewSourceEngineInterfaceToken,
	dataViewTransformInterface: DataViewTransformEngineInterfaceToken,
	dataViewDerivedInterface: DataViewDerivedEngineInterfaceToken,
})

export type AnyToolEngineInterfaceToken = ToolEngineInterfaceToken<BaseToolEngineInterface>
